/**
 * NeoCoreMigracionOnboardingPage
 * ─────────────────────────────────────────────────────────────────
 * Completa el onboarding de los asociados migrados de COBASY que
 * NO tienen kyc_verification ni auth_user. Para cada uno:
 *   1. Operador sube DPI frente/dorso + recibo (PDF o imagen)
 *   2. kyc-ocr-verify extrae datos
 *   3. Valida que el DPI extraído coincida con el del asociado (estricto)
 *   4. Inserta kyc_verification + actualiza telefono_e164
 *   5. Llama admin-credentials.invite → genera PIN
 *   6. Muestra PIN listo para WhatsApp
 *
 * Excluye Kevin Hugo (DPI 2081588981401) — su cuenta es la de
 * Google Play Review y no debe tocarse.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Fingerprint, ArrowLeft, MagnifyingGlass as Search, X, CaretRight,
  IdentificationCard, Receipt, FilePdf, CheckCircle, Warning,
  WhatsappLogo, Copy, Sparkle, Phone, UploadSimple,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { PAISES, PAIS_DEFAULT, detectarPais, digitosNacionales, armarE164, validarNacional, type Pais } from '@/lib/paises'

// ════════════════════════════════════════════════════════════════════
// Tipos
// ════════════════════════════════════════════════════════════════════

type Row = {
  id: string
  neo_id: string
  no_asociado: string
  dpi: string
  nombre_completo: string
  telefono_e164: string | null
  estado_asociado: string
  onboarding_origen: string
  fecha_ingreso: string | null
}

type DocSlot = 'dpi_frente' | 'dpi_dorso' | 'dpi_completo' | 'recibo'

const DOC_LABELS: Record<DocSlot, { titulo: string; sub: string; Icon: typeof IdentificationCard }> = {
  dpi_completo: { titulo: 'DPI · frente + dorso', sub: '1 archivo (foto o PDF, 1 o 2 páginas)', Icon: IdentificationCard },
  dpi_frente:   { titulo: 'DPI · Frente', sub: 'Lado de la foto · imagen o PDF', Icon: IdentificationCard },
  dpi_dorso:    { titulo: 'DPI · Dorso',  sub: 'Lado del MRZ · imagen o PDF', Icon: IdentificationCard },
  recibo:       { titulo: 'Recibo de servicios', sub: 'Agua, luz, internet · imagen o PDF', Icon: Receipt },
}
type DpiInputMode = 'completo' | 'separados'
const ACCEPT_TYPES = 'image/png,image/jpeg,image/webp,application/pdf'
const KEVIN_HUGO_DPI = '2081588981401'

function formatCui(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 13)
  return [d.slice(0, 4), d.slice(4, 9), d.slice(9, 13)].filter(Boolean).join(' ')
}
function isPdf(file: File | null): boolean {
  return !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name))
}

// ════════════════════════════════════════════════════════════════════
// Página principal
// ════════════════════════════════════════════════════════════════════

export function NeoCoreMigracionOnboardingPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [totalCobasy, setTotalCobasy] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Row | null>(null)
  const [bulkPhonesOpen, setBulkPhonesOpen] = useState(false)
  const [pinResult, setPinResult] = useState<{ asociado: Row; pin: string; emailSintetico: string } | null>(null)

  const cargar = useCallback(async () => {
    setLoading(true)
    // Total COBASY migrados (para el denominador del progreso).
    // Excluye a los retirados (decisión operativa: no migrar) para que el
    // porcentaje refleje solo lo que efectivamente hay que procesar.
    const { count: total } = await supabase
      .from('asociado')
      .select('id', { count: 'exact', head: true })
      .eq('onboarding_origen', 'migracion_cobasy')
      .neq('dpi', KEVIN_HUGO_DPI)
      .neq('estado_asociado', 'retirado')
    setTotalCobasy(total ?? 0)

    // Los que aún faltan procesar. Los retirados quedan fuera del listado
    // aunque tengan origen COBASY, porque ya no forman parte de la
    // migración (Hugo los excluyó operativamente).
    const { data } = await supabase
      .from('asociado')
      .select('id, neo_id, no_asociado, dpi, nombre_completo, telefono_e164, estado_asociado, onboarding_origen, fecha_ingreso')
      .eq('onboarding_origen', 'migracion_cobasy')
      .neq('dpi', KEVIN_HUGO_DPI)
      .neq('estado_asociado', 'retirado')
      .is('auth_user_id', null)
      .order('nombre_completo')
    setRows((data as Row[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r =>
      r.nombre_completo.toLowerCase().includes(q) ||
      r.dpi.includes(q) ||
      r.neo_id.includes(q),
    )
  }, [rows, query])

  // Derivadas para el header
  const completados = Math.max(0, totalCobasy - rows.length)
  const pctCompleto = totalCobasy > 0 ? Math.round((completados / totalCobasy) * 100) : 0
  const sinTelefono = rows.filter(r => !r.telefono_e164).length

  return (
    <div className="creds-page">
      <header className="creds-header">
        <div className="creds-title-block">
          <div className="creds-title-icon"><Fingerprint size={22} weight="duotone" /></div>
          <div>
            <h1>Migración onboarding · COBASY → NeoCore</h1>
            <p>Asociados migrados sin KYC verificado ni credencial del app. Subí los documentos (PDF o imagen), confirmá el teléfono y se genera el PIN para enviarles por WhatsApp.</p>
          </div>
        </div>
        <div className="creds-stats">
          <div className="creds-stat">
            <span className="creds-stat-label">Completados</span>
            <span className="creds-stat-num">{completados}/{totalCobasy}</span>
          </div>
          <div className="creds-stat">
            <span className="creds-stat-label">Pendientes</span>
            <span className="creds-stat-num">{rows.length}</span>
          </div>
          {sinTelefono > 0 && (
            <div className="creds-stat">
              <span className="creds-stat-label">Sin teléfono</span>
              <span className="creds-stat-num" style={{ color: 'var(--brand-600, #F04E23)' }}>{sinTelefono}</span>
            </div>
          )}
        </div>
      </header>

      {/* Barra de progreso */}
      {totalCobasy > 0 && (
        <div style={{
          background: 'var(--surface, #fff)', border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 12, padding: 14, marginBottom: 14, display: 'flex',
          alignItems: 'center', gap: 14,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
              <span style={{ fontWeight: 600, color: 'var(--n-700, #374151)' }}>Progreso de la migración</span>
              <span style={{ color: 'var(--muted, #9aa0a6)', fontVariantNumeric: 'tabular-nums' }}>{pctCompleto}%</span>
            </div>
            <div style={{
              width: '100%', height: 8, background: 'var(--n-100, #f3f4f6)',
              borderRadius: 999, overflow: 'hidden',
            }}>
              <div style={{
                width: `${pctCompleto}%`, height: '100%',
                background: 'linear-gradient(90deg, #F04E23 0%, #FF7A4A 100%)',
                transition: 'width .3s ease',
              }} />
            </div>
          </div>
        </div>
      )}

      <div className="creds-filters" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div className="creds-search" style={{ flex: 1 }}>
          <Search size={14} weight="bold" />
          <input
            type="text"
            placeholder="Buscá por nombre, DPI o Neo ID…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button onClick={() => setQuery('')} className="creds-search-clear" title="Limpiar">
              <X size={12} />
            </button>
          )}
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => setBulkPhonesOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          title="Cargá los teléfonos de los 49 asociados de una vez, pegando neo_id + teléfono desde Excel"
        >
          <Phone size={13} weight="bold" />
          Importar teléfonos
        </button>
      </div>

      {loading ? (
        <div className="creds-loading">Cargando padrón…</div>
      ) : filtered.length === 0 ? (
        <div className="creds-empty">
          <CheckCircle size={40} weight="duotone" color="var(--success)" />
          <p>{rows.length === 0 ? '¡Todos migrados! No quedan pendientes.' : 'Sin asociados que coincidan con la búsqueda.'}</p>
        </div>
      ) : (
        <div className="creds-table-wrap">
          <table className="creds-table">
            <thead>
              <tr>
                <th>Asociado</th>
                <th>Neo ID</th>
                <th>Teléfono actual</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} onClick={() => setSelected(r)}>
                  <td>
                    <div className="creds-name">{r.nombre_completo}</div>
                    <div className="creds-dpi">DPI {formatCui(r.dpi)}</div>
                  </td>
                  <td><code className="creds-mono">{r.neo_id}</code></td>
                  <td>{r.telefono_e164 ?? <span className="creds-muted">— sin teléfono</span>}</td>
                  <td><span className="creds-badge tone-warn"><Warning size={12} weight="bold" /> Sin KYC</span></td>
                  <td>
                    <button className="creds-row-cta" onClick={e => { e.stopPropagation(); setSelected(r) }}>
                      Procesar <CaretRight size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer de procesamiento */}
      {selected && (
        <ProcesarDrawer
          asociado={selected}
          onClose={() => setSelected(null)}
          onSuccess={(pin, emailSintetico, telefonoE164) => {
            // Merge del teléfono nuevo al asociado — la fila `selected` tenía
            // el número viejo (o null) porque venía del último `cargar()`.
            // Sin este merge el WhatsApp del PinResultDialog abre el chat
            // con el número obsoleto.
            setPinResult({
              asociado: { ...selected, telefono_e164: telefonoE164 },
              pin,
              emailSintetico,
            })
            setSelected(null)
            void cargar()
          }}
        />
      )}

      {/* Dialog de PIN resultado */}
      {pinResult && (
        <PinResultDialog
          result={pinResult}
          onClose={() => setPinResult(null)}
        />
      )}

      {bulkPhonesOpen && (
        <BulkPhonesModal
          onClose={() => setBulkPhonesOpen(false)}
          onSuccess={() => { setBulkPhonesOpen(false); void cargar() }}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Modal · Importar teléfonos en batch
// ════════════════════════════════════════════════════════════════════

/**
 * Formato esperado (una línea por asociado, separados por espacio, tab o coma):
 *   126500 +50258658811
 *   901155 +50239534331
 *   ...
 *
 * El neo_id puede venir tal cual o con guiones (901155 vs 2025-000001).
 * Números con solo dígitos GT (58658811) se auto-prefijan +502.
 */
function BulkPhonesModal({ onClose, onSuccess }: {
  onClose: () => void
  onSuccess: () => void
}) {
  const [texto, setTexto] = useState('')
  const [busy, setBusy] = useState(false)
  const [resultado, setResultado] = useState<{
    ok: number; noEncontrado: string[]; invalidos: string[]
  } | null>(null)

  const parsear = (input: string): Array<{ neoId: string; telefono: string }> => {
    const lineas = input.split('\n').map(l => l.trim()).filter(Boolean)
    const parsed: Array<{ neoId: string; telefono: string }> = []
    for (const linea of lineas) {
      // Separadores: tab, coma, punto y coma, o espacios
      const partes = linea.split(/[,;\t]|\s+/).map(p => p.trim()).filter(Boolean)
      if (partes.length < 2) continue
      const neoId = partes[0]
      let telefono = partes.slice(1).join('')
      // Normalizar teléfono: si son 8 dígitos y no empieza con +502, agregar +502
      const digits = telefono.replace(/[^\d]/g, '')
      if (digits.length === 8 && !telefono.startsWith('+')) {
        telefono = `+502${digits}`
      } else if (digits.length === 11 && digits.startsWith('502')) {
        telefono = `+${digits}`
      } else if (!telefono.startsWith('+')) {
        telefono = `+${digits}`
      } else {
        telefono = `+${digits}`
      }
      parsed.push({ neoId, telefono })
    }
    return parsed
  }

  const importar = async () => {
    setBusy(true)
    setResultado(null)
    const pares = parsear(texto)
    if (pares.length === 0) {
      setResultado({ ok: 0, noEncontrado: [], invalidos: ['El texto está vacío o no tiene el formato esperado.'] })
      setBusy(false)
      return
    }

    let ok = 0
    const noEncontrado: string[] = []
    const invalidos: string[] = []
    for (const { neoId, telefono } of pares) {
      // Validar E.164 mínimo (+ y al menos 10 dígitos)
      if (!/^\+\d{10,15}$/.test(telefono)) {
        invalidos.push(`${neoId} → "${telefono}" (formato inválido)`)
        continue
      }
      const { error, count } = await supabase
        .from('asociado')
        .update({ telefono_e164: telefono, updated_at: new Date().toISOString() }, { count: 'exact' })
        .eq('neo_id', neoId)
      if (error) {
        invalidos.push(`${neoId} → error: ${error.message}`)
        continue
      }
      if ((count ?? 0) === 0) {
        noEncontrado.push(neoId)
      } else {
        ok++
      }
    }
    setResultado({ ok, noEncontrado, invalidos })
    setBusy(false)
    // Si todo salió bien, cerramos automáticamente después de 2s
    if (ok > 0 && noEncontrado.length === 0 && invalidos.length === 0) {
      setTimeout(onSuccess, 1500)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'grid', placeItems: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)', borderRadius: 14,
          padding: 24, width: 'min(560px, 92vw)', maxHeight: '86vh',
          overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.24)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(240,78,35,0.10)', color: '#F04E23',
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <UploadSimple size={18} weight="duotone" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Importar teléfonos en batch</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted, #6b7280)', lineHeight: 1.5 }}>
              Copiá desde Excel/Sheets: <strong>neo_id</strong> y <strong>teléfono</strong>, una línea por asociado. Los teléfonos guatemaltecos (8 dígitos) se prefijan a +502 automáticamente.
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" title="Cerrar"><X size={14} /></button>
        </div>

        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Pegá el listado (neo_id + teléfono por línea)
        </label>
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          rows={10}
          placeholder="126500 58658811&#10;901155 39534331&#10;450468 +50246523274"
          style={{
            width: '100%', marginTop: 6, padding: 12, border: '1px solid var(--border)',
            borderRadius: 8, fontFamily: 'ui-monospace, monospace', fontSize: 13,
            fontVariantNumeric: 'tabular-nums', resize: 'vertical',
          }}
        />

        {resultado && (
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 8,
            background: resultado.ok > 0 ? 'rgba(15,138,87,0.08)' : 'rgba(212,160,23,0.08)',
            border: `1px solid ${resultado.ok > 0 ? 'rgba(15,138,87,0.24)' : 'rgba(212,160,23,0.24)'}`,
            fontSize: 12,
          }}>
            {resultado.ok > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0F8A57', fontWeight: 600 }}>
                <CheckCircle size={14} weight="fill" />
                {resultado.ok} teléfono{resultado.ok !== 1 ? 's' : ''} actualizado{resultado.ok !== 1 ? 's' : ''} correctamente
              </div>
            )}
            {resultado.noEncontrado.length > 0 && (
              <div style={{ marginTop: 6, color: '#B8860B' }}>
                <strong>{resultado.noEncontrado.length}</strong> no encontrados en el padrón: {resultado.noEncontrado.slice(0, 5).join(', ')}{resultado.noEncontrado.length > 5 ? '…' : ''}
              </div>
            )}
            {resultado.invalidos.length > 0 && (
              <div style={{ marginTop: 6, color: '#B8860B' }}>
                <strong>{resultado.invalidos.length}</strong> con formato inválido:
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {resultado.invalidos.slice(0, 5).map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} className="btn btn-ghost" disabled={busy}>Cancelar</button>
          <button onClick={importar} className="btn btn-primary" disabled={busy || !texto.trim()}>
            {busy ? 'Procesando…' : 'Importar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Drawer · subir docs + procesar
// ════════════════════════════════════════════════════════════════════

type Step = 'capturar' | 'procesando' | 'validando' | 'creando' | 'error'

function ProcesarDrawer({
  asociado, onClose, onSuccess,
}: {
  asociado: Row
  onClose: () => void
  /** Recibe también el teléfono normalizado que se acaba de guardar en la DB,
   *  para que el diálogo de resultado use el número nuevo (no el viejo de la
   *  fila stale). */
  onSuccess: (pin: string, email: string, telefonoE164: string) => void
}) {
  const [step, setStep] = useState<Step>('capturar')
  const [files, setFiles] = useState<Partial<Record<DocSlot, File>>>({})
  const [previewUrls, setPreviewUrls] = useState<Partial<Record<DocSlot, string>>>({})
  // Default 'completo' — más simple para el operador. El toggle en la UI
  // permite cambiar a 'separados' si tiene 2 archivos (frente + dorso).
  const [dpiInputMode, setDpiInputMode] = useState<DpiInputMode>('completo')
  // Selector de país + número. Detecta automáticamente el país del
  // teléfono que ya trae el asociado (o GT default para prospectos nuevos).
  const paisInicial = useMemo(() => detectarPais(asociado.telefono_e164), [asociado.telefono_e164])
  const [pais, setPais] = useState<Pais>(paisInicial)
  const [telefono, setTelefono] = useState(digitosNacionales(asociado.telefono_e164, paisInicial))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => () => Object.values(previewUrls).forEach(u => u && URL.revokeObjectURL(u)), [])

  const setFile = (slot: DocSlot, file: File) => {
    setFiles(prev => {
      const old = previewUrls[slot]
      if (old) URL.revokeObjectURL(old)
      return { ...prev, [slot]: file }
    })
    setPreviewUrls(prev => ({ ...prev, [slot]: URL.createObjectURL(file) }))
    setError(null)
  }

  const removeFile = (slot: DocSlot) => {
    setFiles(prev => { const { [slot]: _, ...r } = prev; return r })
    setPreviewUrls(prev => {
      const u = prev[slot]; if (u) URL.revokeObjectURL(u)
      const { [slot]: _, ...r } = prev
      return r
    })
  }

  // El operador puede subir 1 solo archivo con ambas caras (foto o PDF con 1 o 2
  // páginas) O bien mantener el flujo clásico de 2 archivos separados. El edge
  // function acepta ambas rutas.
  const tieneDpi = dpiInputMode === 'completo'
    ? !!files.dpi_completo
    : !!files.dpi_frente && !!files.dpi_dorso
  const tieneTodo = tieneDpi && !!files.recibo
  const telefonoValido = validarNacional(telefono, pais)
  const puedeAvanzar = tieneTodo && telefonoValido && step === 'capturar'

  const procesar = async () => {
    setError(null)
    setStep('procesando')

    try {
      // 1) OCR
      const fd = new FormData()
      if (dpiInputMode === 'completo') {
        fd.append('dpi_completo', files.dpi_completo!)
      } else {
        fd.append('dpi_frente', files.dpi_frente!)
        fd.append('dpi_dorso', files.dpi_dorso!)
      }
      fd.append('recibo', files.recibo!)
      fd.append('dpi_tipeado', asociado.dpi)  // ayuda al OCR a validar contra el DPI que esperamos
      const { data: ocrData, error: ocrErr } = await supabase.functions.invoke('kyc-ocr-verify', { body: fd })
      if (ocrErr) throw new Error(`OCR: ${ocrErr.message}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ocr = ocrData as any

      // 2) Validar DPI mismatch (estricto)
      setStep('validando')
      const dpiExtraido = String(ocr.numero_documento ?? '').replace(/\D/g, '')
      if (dpiExtraido !== asociado.dpi) {
        throw new Error(
          `El DPI del PDF (${formatCui(dpiExtraido)}) no coincide con el DPI del asociado (${formatCui(asociado.dpi)}). ` +
          `Verificá que los PDFs corresponden a ${asociado.nombre_completo} antes de subir.`,
        )
      }

      // 3) Insertar kyc_verification + actualizar teléfono
      setStep('creando')
      const telefonoE164 = armarE164(pais, telefono)

      const { error: kycErr } = await supabase.from('kyc_verification').insert({
        asociado_id: asociado.id,
        provider: 'MANUAL_OCR',
        provider_request_id: ocr._storage?.kyc_session_id ?? null,
        estado: 'pass',
        dpi_frente_url: ocr._storage?.dpi_frente_url ?? null,
        dpi_dorso_url:  ocr._storage?.dpi_dorso_url ?? null,
        recibo_servicios_url: ocr._storage?.recibo_servicios_url ?? null,
        recibo_servicios_tipo: ocr.recibo_tipo ?? null,
        recibo_servicios_fecha: ocr.recibo_fecha_emision ?? null,
        vigencia_documento: ocr.vigencia_documento ?? null,
        datos_extraidos: ocr,
        payload_provider: { source: 'migracion-cobasy', version: 1 },
        decision_at: new Date().toISOString(),
        motivo_decision: 'Aprobado por operador en migración COBASY',
        enviado_at: new Date().toISOString(),
        respondido_at: new Date().toISOString(),
      })
      if (kycErr) throw new Error(`KYC: ${kycErr.message}`)

      const { error: updErr } = await supabase
        .from('asociado')
        .update({ telefono_e164: telefonoE164 })
        .eq('id', asociado.id)
      if (updErr) throw new Error(`Actualizar teléfono: ${updErr.message}`)

      // Upsert de contactabilidad — sin esto, la ficha del asociado muestra
      // "Sin canales registrados" aunque el teléfono ya esté guardado en
      // `asociado.telefono_e164`. El teléfono nacional (sin +502) se guarda
      // en `telefono_movil`; el E.164 completo en `telefono_movil_e164`.
      const telefonoNacional = telefono  // 8 dígitos
      const { error: contactErr } = await supabase
        .from('contactabilidad')
        .upsert(
          {
            asociado_id: asociado.id,
            telefono_movil: telefonoNacional,
            telefono_movil_e164: telefonoE164,
            whatsapp_optin: true,
            canal_preferido: 'whatsapp',
          },
          { onConflict: 'asociado_id' },
        )
      // No hacemos throw acá — si contactabilidad falla, el asociado igual
      // queda activado. Solo log para no bloquear la migración masiva.
      if (contactErr) console.warn('[migracion] upsert contactabilidad falló:', contactErr.message)

      // 4) Invitar al app vía admin-credentials.invite
      const { data: invData, error: invErr } = await supabase.functions.invoke('admin-credentials', {
        body: { action: 'invite', asociado_id: asociado.id, contacto: telefonoE164 },
      })
      if (invErr) {
        let detail = invErr.message ?? 'desconocido'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx: any = invErr
        if (ctx?.context?.json) {
          try { const j = await ctx.context.json(); detail = j.detail ?? j.error ?? detail } catch {}
        }
        throw new Error(`Invite: ${detail}`)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inv = invData as any
      if (inv?.error) throw new Error(`Invite: ${inv.detail ?? inv.error}`)

      // El email de login es el E.164 sin el '+' (mismo formato que la RPC)
      const emailLogin = telefonoE164.replace(/^\+/, '') + '@neocoop.app'
      onSuccess(inv.pin, emailLogin, telefonoE164)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
      setStep('error')
    }
  }

  return (
    <div className="creds-drawer-overlay" onClick={onClose}>
      <div className="creds-drawer" onClick={e => e.stopPropagation()}>
        <header className="creds-drawer-head">
          <button className="creds-drawer-back" onClick={onClose}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2>{asociado.nombre_completo}</h2>
            <p>Neo ID <code>{asociado.neo_id}</code> · DPI {formatCui(asociado.dpi)}</p>
          </div>
        </header>

        <div className="creds-drawer-body">
          {step !== 'capturar' && step !== 'error' && (
            <div className="paso-procesando" style={{ padding: '32px 16px' }}>
              <div className="iso-spinner" aria-hidden="true">
                <img src="/brand/isotipo-naranja.svg" alt="" />
              </div>
              <h2 style={{ fontSize: 18 }}>
                {step === 'procesando' && 'Procesando documentos del asociado…'}
                {step === 'validando' && 'Validando que el DPI coincide…'}
                {step === 'creando' && 'Creando credenciales y generando PIN…'}
              </h2>
            </div>
          )}

          {step === 'error' && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              <Warning size={18} /> {error}
            </div>
          )}

          {(step === 'capturar' || step === 'error') && (
            <>
              {/* Toggle · DPI en 1 archivo vs frente + dorso separados */}
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  padding: 4,
                  background: 'var(--soft-gray)',
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() => setDpiInputMode('completo')}
                  style={{
                    flex: 1,
                    padding: '7px 10px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: dpiInputMode === 'completo' ? 'white' : 'transparent',
                    color: dpiInputMode === 'completo' ? 'var(--text-primary)' : 'var(--text-secondary)',
                    boxShadow: dpiInputMode === 'completo' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  1 archivo (recomendado)
                </button>
                <button
                  type="button"
                  onClick={() => setDpiInputMode('separados')}
                  style={{
                    flex: 1,
                    padding: '7px 10px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: dpiInputMode === 'separados' ? 'white' : 'transparent',
                    color: dpiInputMode === 'separados' ? 'var(--text-primary)' : 'var(--text-secondary)',
                    boxShadow: dpiInputMode === 'separados' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  Frente + dorso separados
                </button>
              </div>

              <div className="mig-docs-grid">
                {(dpiInputMode === 'completo'
                  ? (['dpi_completo', 'recibo'] as DocSlot[])
                  : (['dpi_frente', 'dpi_dorso', 'recibo'] as DocSlot[])
                ).map(slot => (
                  <MigDropZone
                    key={slot}
                    slot={slot}
                    file={files[slot] || null}
                    previewUrl={previewUrls[slot] || null}
                    onPick={f => setFile(slot, f)}
                    onRemove={() => removeFile(slot)}
                  />
                ))}
              </div>

              <div className="field" style={{ marginTop: 18 }}>
                <label className="field-label">Teléfono móvil <span className="req">*</span></label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Selector de país (GT default, USA/MX/región CA para diáspora) */}
                  <select
                    value={pais.iso}
                    onChange={(e) => {
                      const p = PAISES.find((x) => x.iso === e.target.value) ?? PAIS_DEFAULT
                      setPais(p)
                      setTelefono('') // al cambiar país, limpiamos para no dejar largos de otro
                    }}
                    style={{
                      padding: '9px 11px', border: '1px solid var(--hairline)',
                      borderRadius: 8, fontSize: 13, background: '#fff', color: 'var(--ink)',
                    }}
                  >
                    {PAISES.map((p) => (
                      <option key={p.iso} value={p.iso}>
                        {p.bandera} {p.nombre} (+{p.prefijoDigits})
                      </option>
                    ))}
                  </select>
                  {/* Input del número */}
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                    <div style={{
                      padding: '9px 11px', background: 'var(--soft-gray)', border: '1px solid var(--hairline)',
                      borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span>{pais.bandera}</span>
                      <span>+{pais.prefijoDigits}</span>
                    </div>
                    <input
                      type="text"
                      value={telefono}
                      placeholder={pais.placeholder}
                      maxLength={pais.maxLen}
                      onChange={e => setTelefono(e.target.value.replace(/\D/g, '').slice(0, pais.maxLen))}
                      style={{
                        flex: 1, padding: '9px 11px', border: '1px solid var(--hairline)',
                        borderRadius: 8, fontSize: 13,
                      }}
                    />
                  </div>
                </div>
                <small className="field-hint">
                  {pais.minLen === pais.maxLen ? pais.minLen : `${pais.minLen}-${pais.maxLen}`} dígitos sin código de país. El asociado va a usarlo para entrar al app móvil.
                </small>
              </div>

              <footer className="paso-footer" style={{ marginTop: 18 }}>
                <button className="btn-secondary" onClick={onClose}>Cancelar</button>
                <button
                  className="btn-primary"
                  disabled={!puedeAvanzar}
                  onClick={procesar}
                >
                  <Sparkle size={18} weight="fill" /> Procesar y generar PIN
                </button>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Drop zone reusable (acepta PDF e imagen)
// ════════════════════════════════════════════════════════════════════

function MigDropZone({
  slot, file, previewUrl, onPick, onRemove,
}: {
  slot: DocSlot
  file: File | null
  previewUrl: string | null
  onPick: (file: File) => void
  onRemove: () => void
}) {
  const { titulo, sub, Icon } = DOC_LABELS[slot]
  const [drag, setDrag] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const acceptFile = (f: File) => {
    const ok = f.type === 'application/pdf' || /\.pdf$/i.test(f.name) || f.type.startsWith('image/')
    if (ok) onPick(f)
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false)
    const f = e.dataTransfer.files?.[0]
    if (f) acceptFile(f)
  }
  const pdfMode = isPdf(file)

  return (
    <div
      className={`mig-drop ${drag ? 'drag' : ''} ${file ? 'filled' : ''}`}
      onDragOver={e => { e.preventDefault(); setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_TYPES}
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile(f); if (inputRef.current) inputRef.current.value = '' }}
      />
      {file && previewUrl ? (
        <>
          {pdfMode ? (
            <button className="mig-pdf" onClick={() => window.open(previewUrl, '_blank')}>
              <FilePdf size={36} weight="duotone" />
              <strong>PDF</strong>
            </button>
          ) : (
            <img src={previewUrl} alt={titulo} className="mig-preview" />
          )}
          <div className="mig-overlay">
            <span className="mig-filename">{file.name}</span>
            <button className="mig-remove" onClick={onRemove} title="Quitar"><X size={12} /></button>
          </div>
        </>
      ) : (
        <button className="mig-empty" onClick={() => inputRef.current?.click()}>
          <Icon size={24} weight="duotone" />
          <strong>{titulo}</strong>
          <span>{sub}</span>
        </button>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// Dialog de resultado · PIN listo para WhatsApp
// ════════════════════════════════════════════════════════════════════

function PinResultDialog({
  result, onClose,
}: {
  result: { asociado: Row; pin: string; emailSintetico: string }
  onClose: () => void
}) {
  const [copiado, setCopiado] = useState(false)
  const { asociado, pin } = result
  const telefono = asociado.telefono_e164  // ya actualizado en BD por nuestro flujo
  const primerNombre = asociado.nombre_completo.split(/\s+/)[0]

  const mensajeWhatsApp =
    `¡Hola ${primerNombre}! 🎉\n\n` +
    `Tu cuenta en Neocoop está lista.\n\n` +
    `Para entrar al app:\n` +
    `• Teléfono: ${(telefono ?? '').replace('+502','')}\n` +
    `• PIN provisional: ${pin}\n\n` +
    `Descargá el app desde neocoop.gt/app — al primer login te pediremos cambiar el PIN por uno tuyo.`

  const linkWhatsApp = telefono
    ? `https://wa.me/${telefono.replace('+','')}?text=${encodeURIComponent(mensajeWhatsApp)}`
    : null

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(mensajeWhatsApp)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2200)
    } catch {}
  }

  return (
    <div className="creds-drawer-overlay" onClick={onClose} style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 16, padding: 32, maxWidth: 460,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center',
      }}>
        <div className="check-circle activo" style={{ margin: '0 auto 18px' }}>
          <CheckCircle size={48} weight="fill" />
        </div>
        <h2 style={{ fontSize: 22, marginBottom: 8 }}>{primerNombre} listo para usar el app</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 22 }}>
          Mandale el PIN por WhatsApp ahora. Es la única vez que lo verás.
        </p>

        <div className="cred-card" style={{ minWidth: 0, marginBottom: 22 }}>
          <div className="cred-row"><span>Neo ID</span><strong>{asociado.neo_id}</strong></div>
          <div className="cred-row"><span>Teléfono</span><strong>{telefono}</strong></div>
          <div className="cred-row">
            <span>PIN provisional</span>
            <strong className="pin-big">{pin}</strong>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <button className="btn-secondary" onClick={copiar} style={{ flex: 1 }}>
            <Copy size={16} /> {copiado ? '¡Copiado!' : 'Copiar mensaje'}
          </button>
          {linkWhatsApp && (
            <a className="btn-primary" href={linkWhatsApp} target="_blank" rel="noreferrer" style={{ flex: 1, textDecoration: 'none' }}>
              <WhatsappLogo size={16} weight="fill" /> Abrir WhatsApp
            </a>
          )}
        </div>

        <button className="btn-secondary" onClick={onClose} style={{ width: '100%' }}>
          Cerrar
        </button>
      </div>
    </div>
  )
}
